import { ArrowLeft, ExternalLink, Eye, Loader2, MessageSquareText, Pencil } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  adminClient,
  type StudioPersonActivityPage,
  type StudioPersonActivityQuery,
  type StudioPersonProject,
  type StudioPersonPromptsPage,
  type StudioPersonSignInsResult,
  type StudioPersonSummary,
  type StudioTeamAccessResponse,
  type StudioTeamPerson,
  type StudioTeamRoleKey,
} from '@/api/adminClient';
import { PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { AdminSection } from '@/components/shell/AdminSection';
import { SearchInput } from '@/components/table/SearchInput';
import { TablePager } from '@/components/table/TablePager';
import { Button } from '@/components/ui/button';
import { Sheet } from '@/components/ui/sheet';
import { Thumbnail } from '@/components/ui/thumbnail';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { formatDate, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';

import { PersonOrganizations } from './PersonOrganizations';
import { EditRolesSheet } from './UsersPage';
import {
  ACTIVITY_TYPE_GROUPS,
  activityApiParams,
  activityParams,
  activityQueryFrom,
  activityTypeLabel,
  entryLink,
  entrySourceNote,
  initials,
  isFiltered,
  type PersonTab,
  personTabFrom,
  projectRoleLabel,
  promptModeLabel,
  signInKindLabel,
  signInsProblem,
  teamPersonFrom,
} from './personModel';
import { personLabel, roleCatalog, roleLabel, SEARCH_MIN_CHARS } from './usersModel';

const FIELD_CLASS =
  'h-10 w-full cursor-pointer rounded-md border border-input bg-background px-3 text-base text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-auto sm:text-sm';

const TABS: Array<{ id: PersonTab; label: string }> = [
  { id: 'activity', label: 'Activity' },
  { id: 'organizations', label: 'Organizations' },
  { id: 'projects', label: 'Projects' },
  { id: 'sign-ins', label: 'Sign-ins' },
];

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error && error.message ? error.message : fallback;
}

function Avatar({ label, picture, size = 'lg' }: { label: string; picture?: string; size?: 'lg' | 'sm' }) {
  const box = size === 'lg' ? 'h-14 w-14 text-lg' : 'h-8 w-8 text-xs';
  if (picture) return <img src={picture} alt="" className={cn('shrink-0 rounded-full object-cover', box)} referrerPolicy="no-referrer" />;
  return <span className={cn('flex shrink-0 items-center justify-center rounded-full bg-muted font-semibold text-muted-foreground', box)}>{initials(label)}</span>;
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate text-sm text-foreground">{children}</dd>
    </div>
  );
}

function Problem({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-wrap items-center gap-3 px-4 py-6 text-sm text-destructive-foreground">
      <span className="min-w-0 flex-1">{message}</span>
      {onRetry ? (
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

function Loading({ label }: { label: string }) {
  return (
    <p className="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      {label}
    </p>
  );
}

export type PersonHeaderProps = {
  summary: StudioPersonSummary;
  team?: StudioTeamAccessResponse;
  onEditRoles?: () => void;
};

export function PersonHeader({ summary, team, onEditRoles }: PersonHeaderProps) {
  const catalog = useMemo(() => roleCatalog(team), [team]);
  const label = personLabel(summary.person);
  return (
    <div className="mb-6 flex flex-col gap-4 rounded-xl border border-border bg-card p-5 sm:flex-row sm:items-start">
      <Avatar label={label} picture={summary.person.picture} />
      <div className="min-w-0 flex-1 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-2xl font-bold">{label}</h1>
            {summary.person.email && summary.person.name ? <p className="truncate text-sm text-muted-foreground">{summary.person.email}</p> : null}
          </div>
          {onEditRoles ? (
            <Button type="button" variant="outline" size="sm" onClick={onEditRoles} disabled={!team}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
              Edit roles
            </Button>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {summary.roles === null ? (
            <span className="text-xs text-muted-foreground">Roles could not be read from WorkOS.</span>
          ) : summary.roles.length === 0 ? (
            <span className="text-xs text-muted-foreground">No Studio roles</span>
          ) : (
            summary.roles.map((key) => (
              <span key={key} className="rounded-full border border-border bg-muted/40 px-2.5 py-0.5 text-xs text-foreground">
                {team ? roleLabel(catalog, key) : summary.roleNames[key] ?? key}
              </span>
            ))
          )}
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
          <Fact label="Organizations">{summary.organizations.map((org) => org.name).join(', ') || '—'}</Fact>
          <Fact label="Last sign-in">{summary.lastSignInAt ? formatDateTime(summary.lastSignInAt) : '—'}</Fact>
          <Fact label="Last active">{summary.lastActiveAt ? formatDateTime(summary.lastActiveAt) : '—'}</Fact>
          <Fact label="Member since">{summary.memberSince ? formatDate(summary.memberSince) : '—'}</Fact>
        </dl>
      </div>
    </div>
  );
}

export type ActivityViewProps = {
  query: StudioPersonActivityQuery;
  onQueryChange: (query: StudioPersonActivityQuery) => void;
  search: string;
  onSearchChange: (text: string) => void;
  projects: StudioPersonProject[];
  page?: StudioPersonActivityPage;
  pageIndex: number;
  hasPrev: boolean;
  onPrev: () => void;
  onNext: () => void;
  loading: boolean;
  error?: string;
  onRetry: () => void;
};

export function ActivityView({ query, onQueryChange, search, onSearchChange, projects, page, pageIndex, hasPrev, onPrev, onNext, loading, error, onRetry }: ActivityViewProps) {
  const set = (patch: Partial<StudioPersonActivityQuery>) => onQueryChange({ ...query, ...patch });
  const filtered = isFiltered(query);
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <SearchInput value={search} onChange={onSearchChange} placeholder={`Search activity (${SEARCH_MIN_CHARS}+ letters)`} label="Search activity" />
        <select aria-label="Type" value={query.type ?? ''} onChange={(event) => set({ type: event.target.value || undefined })} className={FIELD_CLASS}>
          <option value="">Everything but presence</option>
          {ACTIVITY_TYPE_GROUPS.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.types.map((type) => (
                <option key={type} value={type}>
                  {activityTypeLabel(type)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
        <select aria-label="Project" value={query.projectId ?? ''} onChange={(event) => set({ projectId: event.target.value || undefined })} className={FIELD_CLASS}>
          <option value="">Any project</option>
          {projects.map((project) => (
            <option key={project.projectId} value={project.projectId}>
              {project.title}
            </option>
          ))}
        </select>
        <label className="flex w-full items-center gap-2 text-xs text-muted-foreground sm:w-auto">
          From
          <input type="date" aria-label="From" value={query.from ?? ''} max={query.to} onChange={(event) => set({ from: event.target.value || undefined })} className={FIELD_CLASS} />
        </label>
        <label className="flex w-full items-center gap-2 text-xs text-muted-foreground sm:w-auto">
          To
          <input type="date" aria-label="To" value={query.to ?? ''} min={query.from} onChange={(event) => set({ to: event.target.value || undefined })} className={FIELD_CLASS} />
        </label>
        {filtered ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => onQueryChange({})}>
            Clear filters
          </Button>
        ) : null}
      </div>
      {loading && !page ? (
        <Loading label="Loading activity" />
      ) : error && !page ? (
        <Problem message={error} onRetry={onRetry} />
      ) : page && page.entries.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">{filtered ? 'No activity matches.' : 'No activity yet.'}</p>
      ) : page ? (
        <ul className={cn('divide-y divide-border', loading && 'opacity-60')} aria-busy={loading}>
          {page.entries.map((entry) => {
            const link = entryLink(entry);
            const note = entrySourceNote(entry);
            const meta = [entry.projectTitle, entry.device, entry.ip, note].filter(Boolean).join(' · ');
            return (
              <li key={entry.id} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-baseline sm:gap-4">
                <span className="shrink-0 text-xs tabular-nums text-muted-foreground sm:w-36">{formatDateTime(entry.at)}</span>
                <span className="shrink-0 sm:w-36">
                  <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-[11px] text-muted-foreground">{activityTypeLabel(entry.type)}</span>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block break-words text-sm text-foreground">{entry.summary}</span>
                  {meta ? <span className="block truncate text-xs text-muted-foreground">{meta}</span> : null}
                </span>
                {link ? (
                  <Link to={link.to} className="shrink-0 cursor-pointer self-start text-xs text-primary underline-offset-2 hover:underline">
                    {link.label}
                  </Link>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}
      {page && (page.entries.length > 0 || hasPrev) ? (
        <TablePager
          mode="cursor"
          pageSize={50}
          loadedCount={page.entries.length}
          hasPrev={hasPrev}
          hasNext={Boolean(page.nextCursor)}
          onPrev={onPrev}
          onNext={onNext}
          label={`Page ${pageIndex + 1}`}
          disabled={loading}
        />
      ) : null}
    </div>
  );
}

export type ProjectsViewProps = {
  projects?: StudioPersonProject[];
  loading: boolean;
  error?: string;
  onRetry: () => void;
  onPrompts: (project: StudioPersonProject) => void;
};

export function ProjectsView({ projects, loading, error, onRetry, onPrompts }: ProjectsViewProps) {
  if (loading && !projects) return <div className="rounded-xl border border-border bg-card"><Loading label="Loading projects" /></div>;
  if (error && !projects) return <div className="rounded-xl border border-border bg-card"><Problem message={error} onRetry={onRetry} /></div>;
  if (!projects || projects.length === 0) return <p className="rounded-xl border border-border bg-card px-4 py-6 text-sm text-muted-foreground">No projects.</p>;
  return (
    <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
      {projects.map((project) => (
        <li key={project.projectId} className="flex flex-col overflow-hidden rounded-xl border border-border bg-card">
          <Thumbnail
            src={project.thumbnailUrl}
            className="aspect-video w-full border-b border-border object-cover object-top"
            fallback={<div className="flex aspect-video w-full items-center justify-center border-b border-border bg-muted/30 text-xs text-muted-foreground">No preview</div>}
          />
          <div className="flex flex-1 flex-col gap-2 p-4">
            <div className="min-w-0">
              <Link
                to={`/organizations/${encodeURIComponent(project.organizationId)}/projects/${encodeURIComponent(project.projectId)}`}
                className="block cursor-pointer truncate font-medium text-foreground underline-offset-2 hover:underline"
              >
                {project.title}
              </Link>
              <p className="truncate text-xs text-muted-foreground">{project.organizationName}</p>
            </div>
            <p className="text-xs text-muted-foreground">
              {projectRoleLabel(project.role)} · {project.versions} version{project.versions === 1 ? '' : 's'} · {project.prompts} prompt{project.prompts === 1 ? '' : 's'}
              {project.updatedAt ? ` · updated ${formatDate(project.updatedAt)}` : ''}
              {project.deleted ? ' · deleted' : ''}
            </p>
            <div className="mt-auto flex flex-wrap gap-2 pt-1">
              {project.openUrl && !project.deleted ? (
                <Button asChild variant="outline" size="sm">
                  <a href={project.openUrl} target="_blank" rel="noopener noreferrer" className="cursor-pointer" aria-label={`Open ${project.title} in the support view`}>
                    <Eye className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                    Open
                    <ExternalLink className="ml-1.5 h-3 w-3" aria-hidden="true" />
                  </a>
                </Button>
              ) : null}
              <Button type="button" variant="outline" size="sm" onClick={() => onPrompts(project)} aria-label={`Prompts in ${project.title}`}>
                <MessageSquareText className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
                Prompts
              </Button>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

export type PromptsSheetProps = {
  project?: StudioPersonProject;
  personName: string;
  page?: StudioPersonPromptsPage;
  loading: boolean;
  error?: string;
  onMore: () => void;
  onClose: () => void;
};

export function PromptsSheet({ project, personName, page, loading, error, onMore, onClose }: PromptsSheetProps) {
  return (
    <Sheet
      open={Boolean(project)}
      onClose={onClose}
      title={project ? `${personName}’s prompts in ${project.title}` : 'Prompts'}
      description="Shown only here. Reading them is recorded and listed on this person’s page."
      className="sm:max-w-2xl"
    >
      {loading && !page ? (
        <Loading label="Loading prompts" />
      ) : error && !page ? (
        <Problem message={error} />
      ) : page && page.prompts.length === 0 ? (
        <p className="text-sm text-muted-foreground">No prompts in this project.</p>
      ) : page ? (
        <div className="space-y-3">
          <ol className="space-y-3">
            {page.prompts.map((prompt) => (
              <li key={prompt.id} className="rounded-lg border border-border p-3">
                <div className="mb-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span className="rounded-full border border-border bg-muted/40 px-2 py-0.5 text-foreground">{promptModeLabel(prompt.mode)}</span>
                  <span className="tabular-nums">{formatDateTime(prompt.at)}</span>
                  {prompt.runStatus ? <span>· {prompt.runStatus.replace(/_/g, ' ')}</span> : null}
                  {prompt.version ? <span>· Version {prompt.version}</span> : null}
                  {prompt.attachments > 0 ? <span>· {prompt.attachments} attachment{prompt.attachments === 1 ? '' : 's'}</span> : null}
                  {prompt.runId ? (
                    <Link
                      to={`/organizations/${encodeURIComponent(page.project.organizationId)}/runs/${encodeURIComponent(prompt.runId)}`}
                      className="ml-auto cursor-pointer text-primary underline-offset-2 hover:underline"
                      onClick={onClose}
                    >
                      {prompt.version ? `Open version ${prompt.version}` : 'Open run'}
                    </Link>
                  ) : null}
                </div>
                <p className="whitespace-pre-wrap break-words text-sm text-foreground">{prompt.text || '—'}</p>
              </li>
            ))}
          </ol>
          {page.nextBefore ? (
            <Button type="button" variant="outline" size="sm" onClick={onMore} disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
              Older prompts
            </Button>
          ) : null}
        </div>
      ) : null}
    </Sheet>
  );
}

export type SignInsViewProps = { result?: StudioPersonSignInsResult; loading: boolean; error?: string; onRetry: () => void };

export function SignInsView({ result, loading, error, onRetry }: SignInsViewProps) {
  const problem = result ? signInsProblem(result) : undefined;
  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <p className="border-b border-border px-4 py-3 text-xs text-muted-foreground">From WorkOS, Studio’s applications only.</p>
      {loading && !result ? (
        <Loading label="Loading sign-ins" />
      ) : error && !result ? (
        <Problem message={error} onRetry={onRetry} />
      ) : problem ? (
        <Problem message={problem} onRetry={onRetry} />
      ) : result && result.entries.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted-foreground">
          {result.lastSignInAt ? `Last signed in ${formatDateTime(result.lastSignInAt)}. WorkOS keeps no sign-in log for Studio to show.` : 'No sign-in recorded yet.'}
        </p>
      ) : result ? (
        <ul className="divide-y divide-border">
          {result.entries.map((entry) => (
            <li key={entry.id} className="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-baseline sm:gap-4">
              <span className="shrink-0 text-xs tabular-nums text-muted-foreground sm:w-36">{formatDateTime(entry.at)}</span>
              <span className={cn('shrink-0 text-sm sm:w-48', entry.result === 'failure' ? 'text-destructive-foreground' : 'text-foreground')}>{signInKindLabel(entry)}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-foreground">
                  {entry.app}
                  {entry.organizationName ? ` · ${entry.organizationName}` : ''}
                </span>
                <span className="block truncate text-xs text-muted-foreground">{[entry.device, entry.ip, entry.connection, entry.reason].filter(Boolean).join(' · ')}</span>
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function ViewedBy({ viewers }: { viewers: StudioPersonSummary['viewedBy'] }) {
  return (
    <AdminSection title="Viewed by" description="Who opened this page or read this person’s prompts, newest first. Each view is recorded.">
      {viewers.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nobody else has looked yet.</p>
      ) : (
        <ul className="space-y-2">
          {viewers.map((viewer) => (
            <li key={viewer.userId} className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
              <span className="min-w-0 truncate text-foreground">{viewer.email ?? viewer.userId}</span>
              <span className="text-xs text-muted-foreground">
                {formatDateTime(viewer.lastAt)} · {viewer.views} view{viewer.views === 1 ? '' : 's'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </AdminSection>
  );
}

/**
 * Studio Admin → Users → one person: who they are, what they did in Studio,
 * their projects (opened in Studio's read-only support view, or their prompts
 * in one) and their sign-ins. Opening the page and reading prompts are
 * recorded and shown under Viewed by.
 */
export function PersonPage() {
  const { userId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = personTabFrom(params.get('tab'));
  const query = useMemo(() => activityQueryFrom(params), [params]);
  const [summary, setSummary] = useState<StudioPersonSummary>();
  const [summaryError, setSummaryError] = useState<string>();
  const [team, setTeam] = useState<StudioTeamAccessResponse>();
  const [editing, setEditing] = useState<StudioTeamPerson>();
  const [saving, setSaving] = useState(false);

  const [search, setSearch] = useState(query.q ?? '');
  const debounced = useDebouncedValue(search.trim());
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [activity, setActivity] = useState<StudioPersonActivityPage>();
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState<string>();
  const activityRequest = useRef(0);

  const [projects, setProjects] = useState<StudioPersonProject[]>();
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string>();

  const [signIns, setSignIns] = useState<StudioPersonSignInsResult>();
  const [signInsLoading, setSignInsLoading] = useState(false);
  const [signInsError, setSignInsError] = useState<string>();

  const [promptsFor, setPromptsFor] = useState<StudioPersonProject>();
  const [prompts, setPrompts] = useState<StudioPersonPromptsPage>();
  const [promptsLoading, setPromptsLoading] = useState(false);
  const [promptsError, setPromptsError] = useState<string>();

  const update = useCallback(
    (nextTab: PersonTab, nextQuery: StudioPersonActivityQuery) => setParams(activityParams(nextTab, nextQuery), { replace: true }),
    [setParams],
  );

  const loadSummary = useCallback(async () => {
    setSummaryError(undefined);
    try {
      setSummary(await adminClient.getPerson(userId));
    } catch (error) {
      setSummaryError(errorText(error, 'Could not load this person.'));
    }
  }, [userId]);

  useEffect(() => {
    void loadSummary();
    adminClient.getTeamAccess().then(setTeam).catch(() => setTeam(undefined));
  }, [loadSummary]);

  const lastSearch = useRef(debounced);
  useEffect(() => {
    if (lastSearch.current === debounced) return;
    lastSearch.current = debounced;
    const q = debounced.length >= SEARCH_MIN_CHARS ? debounced : undefined;
    if (q !== query.q) update(tab, { ...query, q });
  }, [debounced, query, tab, update]);

  const queryKey = JSON.stringify(query);
  useEffect(() => setCursors([undefined]), [queryKey, userId]);
  const cursor = cursors[cursors.length - 1];

  const loadActivity = useCallback(async () => {
    if (!summary) return;
    const id = ++activityRequest.current;
    setActivityLoading(true);
    setActivityError(undefined);
    try {
      const page = await adminClient.getPersonActivity(userId, activityApiParams(query, cursor));
      if (id === activityRequest.current) setActivity(page);
    } catch (error) {
      if (id === activityRequest.current) setActivityError(errorText(error, 'Could not load the activity.'));
    } finally {
      if (id === activityRequest.current) setActivityLoading(false);
    }
  }, [summary, userId, query, cursor]);

  useEffect(() => {
    void loadActivity();
  }, [loadActivity]);

  const loadProjects = useCallback(async () => {
    if (!summary) return;
    setProjectsLoading(true);
    setProjectsError(undefined);
    try {
      setProjects((await adminClient.getPersonProjects(userId)).projects);
    } catch (error) {
      setProjectsError(errorText(error, 'Could not load the projects.'));
    } finally {
      setProjectsLoading(false);
    }
  }, [summary, userId]);

  useEffect(() => {
    void loadProjects();
  }, [loadProjects]);

  const loadSignIns = useCallback(async () => {
    setSignInsLoading(true);
    setSignInsError(undefined);
    try {
      setSignIns(await adminClient.getPersonSignIns(userId));
    } catch (error) {
      setSignInsError(errorText(error, 'Could not load the sign-ins.'));
    } finally {
      setSignInsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (tab === 'sign-ins' && summary && !signIns && !signInsLoading) void loadSignIns();
  }, [tab, summary, signIns, signInsLoading, loadSignIns]);

  const loadPrompts = useCallback(
    async (project: StudioPersonProject, before?: string) => {
      setPromptsLoading(true);
      setPromptsError(undefined);
      try {
        const page = await adminClient.getPersonPrompts(userId, project.projectId, before);
        setPrompts((current) => (before && current ? { ...page, prompts: [...current.prompts, ...page.prompts] } : page));
      } catch (error) {
        setPromptsError(errorText(error, 'Could not load the prompts.'));
      } finally {
        setPromptsLoading(false);
      }
    },
    [userId],
  );

  const openPrompts = (project: StudioPersonProject) => {
    setPromptsFor(project);
    setPrompts(undefined);
    void loadPrompts(project);
  };

  const saveRoles = async (person: StudioTeamPerson, change: { add: StudioTeamRoleKey[]; remove: StudioTeamRoleKey[] }) => {
    setSaving(true);
    try {
      const result = await adminClient.setTeamPersonRoles(person.userId, change);
      if (result.failed.length > 0) toast.error(result.failed.map((failure) => failure.error).join(' '));
      else toast.success('Roles saved');
      setEditing(undefined);
      await loadSummary();
    } catch (error) {
      toast.error(errorText(error, 'Could not save the roles.'));
    } finally {
      setSaving(false);
    }
  };

  const name = summary ? personLabel(summary.person) : '';

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <Link to="/users" className="mb-3 inline-flex cursor-pointer items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Users
      </Link>
      {summaryError ? (
        <div className="rounded-xl border border-border bg-card">
          <Problem message={summaryError} onRetry={() => void loadSummary()} />
        </div>
      ) : !summary ? (
        <div className="rounded-xl border border-border bg-card">
          <Loading label="Loading person" />
        </div>
      ) : (
        <>
          <PersonHeader summary={summary} team={team} onEditRoles={() => setEditing(teamPersonFrom(summary))} />
          <div role="tablist" aria-label="Sections" className="mb-4 inline-flex rounded-lg border border-border bg-muted/40 p-0.5">
            {TABS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                role="tab"
                aria-selected={tab === entry.id}
                onClick={() => update(entry.id, query)}
                className={cn(
                  'inline-flex h-9 cursor-pointer items-center rounded-md px-4 text-sm font-medium transition-colors',
                  tab === entry.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {entry.label}
              </button>
            ))}
          </div>
          <div className="space-y-6">
            {tab === 'activity' ? (
              <ActivityView
                query={query}
                onQueryChange={(next) => {
                  if (next.q === undefined && query.q) setSearch('');
                  update('activity', next);
                }}
                search={search}
                onSearchChange={setSearch}
                projects={projects ?? []}
                page={activity}
                pageIndex={cursors.length - 1}
                hasPrev={cursors.length > 1}
                onPrev={() => setCursors((current) => current.slice(0, -1))}
                onNext={() => activity?.nextCursor && setCursors((current) => [...current, activity.nextCursor])}
                loading={activityLoading}
                error={activityError}
                onRetry={() => void loadActivity()}
              />
            ) : tab === 'organizations' ? (
              <PersonOrganizations userId={summary.person.userId} personName={name} />
            ) : tab === 'projects' ? (
              <ProjectsView projects={projects} loading={projectsLoading} error={projectsError} onRetry={() => void loadProjects()} onPrompts={openPrompts} />
            ) : (
              <SignInsView result={signIns} loading={signInsLoading} error={signInsError} onRetry={() => void loadSignIns()} />
            )}
            <ViewedBy viewers={summary.viewedBy} />
          </div>
          <PromptsSheet
            project={promptsFor}
            personName={name}
            page={prompts}
            loading={promptsLoading}
            error={promptsError}
            onMore={() => promptsFor && prompts?.nextBefore && void loadPrompts(promptsFor, prompts.nextBefore)}
            onClose={() => setPromptsFor(undefined)}
          />
          <EditRolesSheet person={editing} catalog={roleCatalog(team)} saving={saving} onClose={() => setEditing(undefined)} onSave={(person, change) => void saveRoles(person, change)} />
        </>
      )}
    </div>
  );
}

